import React from 'react';
import { panelClass } from '../../governed-mcp-gateway/components/GatewayUi';
import { apiTypeLabel, authLabel, llmTypeLabel, providerLabel } from '../llmGatewayConstants';
import type { GatewayFormState, RegisteredLlm } from '../llmGatewayTypes';

function Row({ label, value, isLight }: { label: string; value: React.ReactNode; isLight: boolean }) {
  return (
    <div className={`flex items-start justify-between gap-4 py-2.5 border-b last:border-b-0 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
      <dt className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</dt>
      <dd className={`text-sm text-right font-medium whitespace-pre-wrap break-words max-w-[22rem] ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{value}</dd>
    </div>
  );
}

export default function ReviewStep({
  form,
  isLight,
  models,
}: {
  form: GatewayFormState;
  isLight: boolean;
  models: RegisteredLlm[];
}) {
  const model = models.find((item) => item.id === form.modelId);
  const g = form.governance;

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <section className={`${panelClass(isLight)} p-5`}>
        <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Gateway</h3>
        <dl className="mt-3">
          <Row label="Gateway Name" value={form.name || '—'} isLight={isLight} />
          <Row label="LLM Type" value={form.llmType ? llmTypeLabel(form.llmType) : '—'} isLight={isLight} />
          <Row label="Registered LLM" value={model?.name ?? '—'} isLight={isLight} />
          <Row label="Provider" value={model ? providerLabel(model.provider) : '—'} isLight={isLight} />
          <Row
            label="Gateway API Types"
            value={form.apiTypes.length ? form.apiTypes.map(apiTypeLabel).join(', ') : '—'}
            isLight={isLight}
          />
          {form.apiTypes.includes('custom') ? <Row label="Custom endpoint" value={form.customEndpoint || '—'} isLight={isLight} /> : null}
          <Row label="Authentication" value={authLabel(form.authMethod)} isLight={isLight} />
        </dl>
      </section>
      <section className={`${panelClass(isLight)} p-5`}>
        <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Governance</h3>
        <dl className="mt-3">
          <Row label="Governance" value={g.enabled ? 'Enabled' : 'Disabled'} isLight={isLight} />
          <Row label="Rate Limiting" value={g.rateLimiting ? `${g.rateLimitPerMinute} req/min` : 'Off'} isLight={isLight} />
          <Row label="Token Quotas" value={g.tokenQuotas ? `${g.tokenQuotaMonthly.toLocaleString()} / month` : 'Off'} isLight={isLight} />
          <Row label="Audit Logging" value={g.auditLogging ? 'On' : 'Off'} isLight={isLight} />
          <Row label="PII Detection" value={g.piiDetection ? g.piiMode : 'Off'} isLight={isLight} />
          <Row label="Content Filtering" value={g.contentFiltering ? g.contentFilterMode : 'Off'} isLight={isLight} />
          <Row
            label="Gateway Instructions"
            value={g.gatewayInstructions ? (g.gatewayInstructionsText.trim() || 'On') : 'Off'}
            isLight={isLight}
          />
        </dl>
      </section>
    </div>
  );
}
